import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { emergencySafetyApi } from "../services/api";
import ModuleAskBar from "../components/shared/ModuleAskBar";
import SheetHandle from "../components/shared/SheetHandle";
import ConfirmModal from "../components/shared/ConfirmModal";
import SavingNotice from "../components/shared/SavingNotice";
import useMinLoad from "../hooks/useMinLoad";
import "./EmergencySafetyPage.css";

const EMPTY_RESOURCE = {
  label: "",
  resource_type: "water_shutoff",
  property_space_id: "",
  vault_item_id: "",
  instructions: "",
  access_notes: "",
  verification_interval_days: "180",
};
const EMPTY_CONTACT = {
  name: "",
  relationship: "",
  phone: "",
  email: "",
  contact_type: "household",
  person_id: "",
};
const EMPTY_DRILL = { title: "Household evacuation drill", drill_type: "evacuation", scheduled_on: "", repeat_interval_days: "180", notes: "" };
function offlinePlanKey(workspaceId = "current") {
  let userId = "signed-in";
  try {
    const token = localStorage.getItem("hm_access_token") || "";
    const payload = JSON.parse(atob((token.split(".")[1] || "").replace(/-/g, "+").replace(/_/g, "/")));
    userId = payload.sub || payload.identity || userId;
  } catch {}
  return `housemind_emergency_plan_v1:${userId}:${workspaceId}`;
}
const TYPES = {
  water_shutoff: "Water shutoff",
  gas_shutoff: "Gas shutoff",
  electrical_panel: "Electrical panel",
  fire_extinguisher: "Fire extinguisher",
  smoke_alarm: "Smoke alarm",
  co_alarm: "CO alarm",
  first_aid: "First aid",
  exit: "Emergency exit",
  shelter: "Shelter location",
  other: "Other",
};
const RELATIONSHIP_SUGGESTIONS = [
  "Spouse",
  "Partner",
  "Parent",
  "Child",
  "Sibling",
  "Grandparent",
  "Neighbor",
  "Friend",
  "Family doctor",
  "Veterinarian",
  "Landlord",
  "Property manager",
  "Babysitter",
  "Pet sitter",
  "Plumber",
  "Electrician",
  "HOA contact",
  "Employer",
  "Poison control",
];
const RESOURCE_LABEL_SUGGESTIONS = {
  water_shutoff: [
    "Main water shutoff",
    "Outdoor spigot shutoff",
    "Upstairs bathroom shutoff",
  ],
  gas_shutoff: ["Main gas shutoff", "Gas meter shutoff"],
  electrical_panel: ["Main electrical panel", "Sub panel"],
  fire_extinguisher: [
    "Kitchen fire extinguisher",
    "Garage fire extinguisher",
    "Hallway fire extinguisher",
    "Basement fire extinguisher",
  ],
  smoke_alarm: [
    "Hallway smoke alarm",
    "Bedroom smoke alarm",
    "Kitchen smoke alarm",
    "Basement smoke alarm",
  ],
  co_alarm: ["Hallway CO alarm", "Basement CO alarm", "Bedroom CO alarm"],
  first_aid: [
    "Kitchen first aid kit",
    "Bathroom first aid kit",
    "Car first aid kit",
  ],
  exit: ["Front door", "Back door", "Bedroom window exit", "Basement exit"],
  shelter: ["Basement safe room", "Interior bathroom", "Storm shelter"],
};

function formatDate(value) {
  if (!value) return "Not scheduled";
  return new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function verificationLabel(row) {
  if (row.verification_status === "unverified") return "Verify now to start checks";
  if (row.verification_status === "overdue") return `Overdue since ${formatDate(row.next_verification_on)}`;
  if (row.verification_status === "due") return "Verification due today";
  if (row.verification_status === "upcoming") return `Next check ${formatDate(row.next_verification_on)}`;
  if (row.verification_status === "current") return `Current · next ${formatDate(row.next_verification_on)}`;
  return "No recurring check";
}

function emergencyPlanText(data) {
  const plan = data.plan || {};
  const contacts = data.contacts || [];
  const resources = data.resources || [];
  return [
    "HOUSEMIND EMERGENCY PLAN",
    `Primary meeting point: ${plan.primary_meeting_point || "Not saved"}`,
    `Alternate meeting point: ${plan.alternate_meeting_point || "Not saved"}`,
    `Evacuation: ${plan.evacuation_notes || "Not saved"}`,
    `Shelter: ${plan.shelter_notes || "Not saved"}`,
    "",
    "CONTACTS",
    ...(contacts.length ? contacts.map((row) => `${row.name}: ${row.phone || row.email || "No contact details"}`) : ["No contacts saved"]),
    "",
    "CRITICAL LOCATIONS",
    ...(resources.length ? resources.map((row) => `${row.label}: ${row.property_space_name || row.access_notes || "Location not saved"}${row.instructions ? ` — ${row.instructions}` : ""}`) : ["No locations saved"]),
  ].join("\n");
}

export default function EmergencySafetyPage() {
  const [data, setData] = useState(null),
    [plan, setPlan] = useState({});
  const [resource, setResource] = useState(EMPTY_RESOURCE),
    [contact, setContact] = useState(EMPTY_CONTACT);
  const [drill, setDrill] = useState(EMPTY_DRILL);
  const [scanConsent, setScanConsent] = useState(false);
  const [offlineCopy, setOfflineCopy] = useState(false);
  const scanInputRef = useRef(null), scanCameraRef = useRef(null);
  const [sheet, setSheet] = useState(null);
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [guideExpanded, setGuideExpanded] = useState(false);
  const [shareNotice, setShareNotice] = useState("");
  const [contactToRemove, setContactToRemove] = useState(null);
  const [removePlanOpen, setRemovePlanOpen] = useState(false);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const next = await emergencySafetyApi.snapshot();
      setData(next);
      setPlan(next.plan || {});
      localStorage.setItem(offlinePlanKey(next.plan?.workspace_id || "current"), JSON.stringify(next));
      localStorage.setItem(offlinePlanKey(), JSON.stringify(next));
      setOfflineCopy(false);
    } catch (err) {
      try {
        const cached = JSON.parse(localStorage.getItem(offlinePlanKey()) || "null");
        if (cached?.plan) { setData(cached); setPlan(cached.plan); setOfflineCopy(true); setError("Offline copy — reconnect before making changes."); return; }
      } catch {}
      setError(err.message || "Safety information could not be loaded.");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!emergencyMode) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("hm-print-emergency-ready");
    const onKeyDown = (event) => {
      if (event.key === "Escape") setEmergencyMode(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("hm-print-emergency-ready");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [emergencyMode]);
  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
      await load();
      return true;
    } catch (err) {
      setError(err.message || "That change could not be saved.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const savePlan = async (event) => {
    event.preventDefault();
    if (
      await run(() =>
        emergencySafetyApi.updatePlan({
          ...plan,
          last_reviewed_on: new Date().toLocaleDateString("en-CA"),
        }),
      )
    )
      setSheet(null);
  };
  const showSavingNotice = useMinLoad(busy && data !== null, 700);
  const addResource = async (event) => {
    event.preventDefault();
    const action = resource.id
      ? () => emergencySafetyApi.updateResource(resource.id, resource)
      : () => emergencySafetyApi.createResource(resource);
    if (await run(action)) {
      setResource(EMPTY_RESOURCE);
      setSheet(null);
    }
  };
  const addContact = async (event) => {
    event.preventDefault();
    const action = contact.id
      ? () => emergencySafetyApi.updateContact(contact.id, contact)
      : () => emergencySafetyApi.createContact(contact);
    if (await run(action)) {
      setContact(EMPTY_CONTACT);
      setSheet(null);
    }
  };
  const saveDrill = async (event) => {
    event.preventDefault();
    const action = drill.id ? () => emergencySafetyApi.updateDrill(drill.id, drill) : () => emergencySafetyApi.createDrill(drill);
    if (await run(action)) { setDrill(EMPTY_DRILL); setSheet(null); }
  };
  const scanResource = async (file) => {
    if (!file || !scanConsent) return;
    setBusy(true); setError("");
    try {
      const result = await emergencySafetyApi.scanResource(file);
      if (!result.ai_configured) throw new Error(result.message || "AI scanning is not configured.");
      setResource({ ...EMPTY_RESOURCE, ...(result.resource || {}) });
      setSheet("resource");
      if ((result.warnings || []).length) setError(result.warnings.join(" "));
    } catch (err) { setError(err.message || "That photo could not be scanned."); }
    finally { setBusy(false); }
  };
  const addSuggestedContact = (suggestion) =>
    run(() =>
      emergencySafetyApi.createContact({
        person_id: suggestion.person_id,
        name: suggestion.name,
        relationship: suggestion.relationship,
      }),
    );
  const addSuggestedResource = (suggestion) =>
    run(() =>
      emergencySafetyApi.createResource({
        vault_item_id: suggestion.vault_item_id,
        label: suggestion.label,
        resource_type: suggestion.resource_type,
      }),
    );
  const openResource = (row = null) => {
    setResource(row ? {
      ...EMPTY_RESOURCE,
      ...row,
      property_space_id: row.property_space_id || "",
      vault_item_id: row.vault_item_id || "",
      verification_interval_days: row.verification_interval_days ?? "",
    } : EMPTY_RESOURCE);
    setSheet("resource");
  };
  const openContact = (row = null) => {
    setContact(row ? {
      ...EMPTY_CONTACT,
      ...row,
      person_id: row.person_id || "",
      phone: row.phone_override || "",
      email: row.email_override || "",
    } : EMPTY_CONTACT);
    setSheet("contact");
  };
  const openChecklistAction = (action) => {
    if (action === "plan") setSheet("plan");
    if (action === "resource") openResource();
    if (action === "contact") openContact();
  };
  const sharePlan = async () => {
    const text = emergencyPlanText(data);
    try {
      if (navigator.share) {
        await navigator.share({ title: "HouseMind emergency plan", text });
        setShareNotice("Plan shared.");
      } else {
        await navigator.clipboard.writeText(text);
        setShareNotice("Plan copied.");
      }
    } catch (err) {
      if (err?.name !== "AbortError") setShareNotice("Could not share this plan.");
    }
  };
  if (!data)
    return (
      <main className="hm-safety-page">
        <div className="hm-safety-loading">
          Preparing household safety context…
        </div>
      </main>
    );
  const sortedChecklist = [...(data.checklist || [])].sort(
    (a, b) => Number(a.complete) - Number(b.complete),
  );
  const visibleChecklist = guideExpanded
    ? sortedChecklist
    : sortedChecklist.slice(0, 3);
  const hiddenChecklistCount = Math.max(0, sortedChecklist.length - 3);
  return (
    <main className="hm-safety-page">
      <datalist id="hm-safety-label-suggestions">
        {(RESOURCE_LABEL_SUGGESTIONS[resource.resource_type] || []).map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      <datalist id="hm-safety-relationship-suggestions">
        {RELATIONSHIP_SUGGESTIONS.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      <section className="hm-safety-hero">
        <div>
          <span>Emergency & safety</span>
          <h1>Know where. Know who. Act calmly.</h1>
          <p>
            One practical plan connected to the rooms, equipment, and
            maintenance records HouseMind already knows.
          </p>
        </div>
        <div className="hm-safety-score">
          <strong>{data.readiness}%</strong>
          <small>ready</small>
        </div>
      </section>
      {error && <div className="hm-safety-error">{error}</div>}
      {offlineCopy && <div className="hm-safety-offline">Saved emergency plan available offline · last synced copy</div>}
      <section className="hm-safety-summary">
        <div>
          <strong>{data.summary.resources}</strong>
          <span>locations</span>
        </div>
        <div>
          <strong>{data.summary.contacts}</strong>
          <span>contacts</span>
        </div>
        <div className={data.summary.checks_due ? "needs-care" : ""}>
          <strong>{data.summary.checks_due}</strong>
          <span>checks due</span>
        </div>
      </section>

      <section className="hm-safety-card hm-safety-guide">
        <header>
          <div>
            <span>Guided readiness</span>
            <h2>Your next safety steps</h2>
          </div>
          <em>{data.summary.checklist_complete}/{data.summary.checklist_total} complete</em>
        </header>
        <p className="hm-safety-guide-copy">
          This score measures saved, reachable, and recently verified information—not whether a home is risk-free.
        </p>
        <div className={`hm-safety-checklist-drawer${guideExpanded ? " is-open" : ""}`}>
          <div className="hm-safety-checklist">
            {visibleChecklist.map((item) => (
              <article className={item.complete ? "is-complete" : ""} key={item.key}>
                <i aria-hidden="true">{item.complete ? "✓" : "○"}</i>
                <div><strong>{item.label}</strong><span>{item.detail}</span></div>
                {!item.complete && <button type="button" onClick={() => openChecklistAction(item.action)}>Add</button>}
              </article>
            ))}
          </div>
          {hiddenChecklistCount > 0 && (
            <button
              className="hm-safety-checklist-more"
              type="button"
              aria-expanded={guideExpanded}
              onClick={() => setGuideExpanded((value) => !value)}
            >
              <span>{guideExpanded ? "Show less" : `More · ${hiddenChecklistCount}`}</span>
              <i aria-hidden="true">{guideExpanded ? "⌃" : "⌄"}</i>
            </button>
          )}
        </div>
      </section>

      <section className="hm-safety-card">
        <header>
          <div>
            <span>Household plan</span>
            <h2>Meet, leave, and shelter</h2>
          </div>
          <div className="hm-safety-plan-actions">
            <button className="hm-safety-emergency-launch" type="button" onClick={() => setEmergencyMode(true)}>Emergency mode</button>
            <button className="hm-safety-open-sheet" type="button" onClick={() => setSheet("plan")}>
              {plan.confirmation_status === "confirmed" ? "Edit plan" : "Set up"}
            </button>
          </div>
        </header>
        <div className="hm-safety-plan-preview">
          <strong>
            {plan.primary_meeting_point || "No meeting point saved"}
          </strong>
          <span>
            {plan.confirmation_status === "confirmed"
              ? `${plan.review_status === "overdue" ? "Review overdue" : "Plan reviewed"} · next ${formatDate(plan.next_review_on)}`
              : "Add evacuation and shelter details"}
          </span>
        </div>
      </section>

      <section className="hm-safety-card hm-safety-scan-card">
        <header><div><span>Photo discovery</span><h2>Identify a safety location</h2></div></header>
        <p>Photograph a shutoff, panel, alarm, extinguisher, or first-aid kit. HouseMind will prefill a review form and will never save the result automatically.</p>
        <label className="hm-safety-scan-consent"><input type="checkbox" checked={scanConsent} onChange={(event) => setScanConsent(event.target.checked)} /> I consent to sending this photo to the configured AI provider for analysis.</label>
        <div className="hm-safety-scan-actions">
          <input ref={scanInputRef} type="file" accept="image/*" hidden onChange={(event) => { scanResource(event.target.files?.[0]); event.target.value = ""; }} />
          <input ref={scanCameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(event) => { scanResource(event.target.files?.[0]); event.target.value = ""; }} />
          <button type="button" disabled={!scanConsent || busy || offlineCopy} onClick={() => scanCameraRef.current?.click()}>📷 Use camera</button>
          <button type="button" className="quiet" disabled={!scanConsent || busy || offlineCopy} onClick={() => scanInputRef.current?.click()}>Upload photo</button>
        </div>
      </section>

      <section className="hm-safety-card">
        <header>
          <div><span>Practice</span><h2>Household safety drills</h2></div>
          <button className="hm-safety-open-sheet" type="button" disabled={offlineCopy} onClick={() => { setDrill(EMPTY_DRILL); setSheet("drill"); }}>+ Schedule</button>
        </header>
        <div className="hm-safety-drills">
          {(data.drills || []).length ? data.drills.map((row) => <article key={row.id} className={row.status === "completed" ? "is-complete" : ""}>
            <div><strong>{row.title}</strong><span>{row.status === "completed" ? `Completed ${formatDate(row.completed_on)}` : `Scheduled ${formatDate(row.scheduled_on)}`}{row.repeat_interval_days ? ` · repeats every ${row.repeat_interval_days} days` : ""}</span>{row.notes && <small>{row.notes}</small>}</div>
            {row.status === "scheduled" && <><button type="button" disabled={busy || offlineCopy} onClick={() => run(() => emergencySafetyApi.completeDrill(row.id))}>Mark complete</button><button type="button" className="quiet" disabled={busy || offlineCopy} onClick={() => { setDrill(row); setSheet("drill"); }}>Edit</button><button type="button" className="quiet" disabled={busy || offlineCopy} onClick={() => run(() => emergencySafetyApi.deleteDrill(row.id))}>Cancel</button></>}
          </article>) : <p className="hm-safety-empty-copy">No drills scheduled. A short practice helps everyone use the saved plan under pressure.</p>}
        </div>
      </section>

      <section className="hm-safety-card">
        <header>
          <div>
            <span>Critical locations</span>
            <h2>Controls and safety equipment</h2>
          </div>
          <button
            className="hm-safety-open-sheet"
            type="button"
            onClick={() => openResource()}
          >
            + Add
          </button>
        </header>
        <div className="hm-safety-resources">
          {data.resources.map((row) => (
            <article key={row.id}>
              <div className="hm-safety-resource-icon">
                {row.resource_type.includes("shutoff")
                  ? "⏻"
                  : row.resource_type.includes("alarm")
                    ? "◉"
                    : "✚"}
              </div>
              <div>
                <strong>{row.label}</strong>
                <span>
                  {row.property_space_name || "Location not assigned"}
                  {row.vault_item_name ? ` · ${row.vault_item_name}` : ""}
                </span>
                {row.access_notes && <small>{row.access_notes}</small>}
                <small className={`hm-safety-verification is-${row.verification_status}`}>
                  {verificationLabel(row)}
                </small>
              </div>
              <button className="quiet" disabled={busy} onClick={() => openResource(row)}>Edit</button>
              <button
                disabled={busy}
                onClick={() =>
                  run(() => emergencySafetyApi.verifyResource(row.id))
                }
              >
                Verify
              </button>
              <button
                className="quiet"
                disabled={busy}
                onClick={() =>
                  run(() => emergencySafetyApi.deleteResource(row.id))
                }
              >
                Remove
              </button>
            </article>
          ))}
        </div>
        {data.suggested_resources && data.suggested_resources.length > 0 && (
          <div className="hm-safety-suggest">
            <span>Already in Home Vault</span>
            <div className="hm-safety-suggest-chips">
              {data.suggested_resources.map((s) => (
                <button
                  type="button"
                  key={s.vault_item_id}
                  disabled={busy}
                  onClick={() => addSuggestedResource(s)}
                >
                  + {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="hm-safety-card">
        <header>
          <div>
            <span>Contacts</span>
            <h2>People to reach quickly</h2>
          </div>
          <button
            className="hm-safety-open-sheet"
            type="button"
            onClick={() => openContact()}
          >
            + Add
          </button>
        </header>
        <div className="hm-safety-contacts">
          {data.contacts.map((row) => (
            <article key={row.id}>
              <div>
                <strong>{row.name}</strong>
                <span>
                  {row.relationship || row.contact_type}
                  {row.contact_source === "people" ? " · From People" : ""}
                </span>
              </div>
              <div>
                {row.phone ? <a href={`tel:${row.phone}`}>{row.phone}</a> : <small className="hm-safety-missing">Phone needed</small>}
                {row.email && <small>{row.email}</small>}
              </div>
              <button className="quiet" disabled={busy} onClick={() => openContact(row)}>Edit</button>
              <button
                className="quiet"
                disabled={busy}
                onClick={() => setContactToRemove(row)}
              >
                Remove
              </button>
            </article>
          ))}
        </div>
        {data.suggested_contacts && data.suggested_contacts.length > 0 && (
          <div className="hm-safety-suggest">
            <span>Already in People</span>
            <div className="hm-safety-suggest-chips">
              {data.suggested_contacts.map((s) => (
                <button
                  type="button"
                  key={s.person_id}
                  disabled={busy}
                  onClick={() => addSuggestedContact(s)}
                >
                  + {s.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
      <aside className="hm-safety-note">
        <strong>HouseMind does not replace emergency services.</strong>
        <span>
          For an active emergency, follow local guidance and contact emergency
          services directly.
        </span>
      </aside>
      <ModuleAskBar
        module="emergency_safety"
        label="Emergency & Safety"
        placeholder="Ask about household safety…"
      />
      {emergencyMode && createPortal(
        <div className="hm-emergency-mode-bg">
          <section className="hm-emergency-mode" role="dialog" aria-modal="true" aria-labelledby="hm-emergency-mode-title">
            <header>
              <div><span>Emergency mode</span><h2 id="hm-emergency-mode-title">Act from the saved plan</h2></div>
              <button type="button" aria-label="Close emergency mode" onClick={() => setEmergencyMode(false)}>×</button>
            </header>
            <div className="hm-emergency-warning">
              <strong>For immediate danger, contact local emergency services.</strong>
              <span>HouseMind organizes saved information; it does not monitor or dispatch help.</span>
            </div>
            <div className="hm-emergency-mode-body">
              <section><span>Meet here</span><strong>{plan.primary_meeting_point || "Primary meeting point not saved"}</strong>{plan.alternate_meeting_point && <small>Backup: {plan.alternate_meeting_point}</small>}</section>
              <section><span>Leave safely</span><strong>{plan.evacuation_notes || "Evacuation instructions not saved"}</strong></section>
              <section><span>Shelter</span><strong>{plan.shelter_notes || "Shelter instructions not saved"}</strong></section>
              <section className="wide"><span>Call</span><div className="hm-emergency-call-list">{data.contacts.length ? data.contacts.map((row) => <article key={row.id}><div><strong>{row.name}</strong><small>{row.relationship || row.contact_type}</small></div>{row.phone ? <a href={`tel:${row.phone}`}>Call {row.phone}</a> : <em>No phone</em>}</article>) : <p>No emergency contacts saved.</p>}</div></section>
              <section className="wide"><span>Critical locations</span><div className="hm-emergency-resource-list">{data.resources.length ? data.resources.map((row) => <article key={row.id}><strong>{row.label}</strong><b>{row.property_space_name || row.access_notes || "Location not saved"}</b>{row.access_notes && row.property_space_name && <small>{row.access_notes}</small>}{row.instructions && <small>{row.instructions}</small>}</article>) : <p>No critical locations saved.</p>}</div></section>
            </div>
            <footer>
              <span aria-live="polite">{shareNotice}</span>
              <button className="quiet" type="button" onClick={() => window.print()}>Print</button>
              <button type="button" onClick={sharePlan}>Share plan</button>
            </footer>
          </section>
        </div>, document.body,
      )}
      {sheet && (
        <div
          className="hm-safety-sheet-bg"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSheet(null);
          }}
        >
          <section className="hm-safety-sheet" role="dialog" aria-modal="true" aria-labelledby="hm-safety-sheet-title">
            <SheetHandle onClose={() => setSheet(null)} label="Close emergency and safety form" />
            <header>
              <div>
                <span>Emergency & safety</span>
                <h2 id="hm-safety-sheet-title">
                  {sheet === "plan"
                    ? "Household plan"
                    : sheet === "resource"
                      ? resource.id ? "Edit safety location" : "Add safety location"
                      : sheet === "contact"
                        ? contact.id ? "Edit emergency contact" : "Add emergency contact"
                        : drill.id ? "Edit safety drill" : "Schedule safety drill"}
                </h2>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setSheet(null)}
              >
                ×
              </button>
            </header>
            {sheet === "plan" && (
              <form className="hm-safety-form" onSubmit={savePlan}>
                <label>
                  Primary meeting point
                  <input
                    value={plan.primary_meeting_point || ""}
                    placeholder="Across the street by…"
                    onChange={(e) =>
                      setPlan((v) => ({
                        ...v,
                        primary_meeting_point: e.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Alternate meeting point
                  <input
                    value={plan.alternate_meeting_point || ""}
                    placeholder="If the neighborhood is blocked…"
                    onChange={(e) =>
                      setPlan((v) => ({
                        ...v,
                        alternate_meeting_point: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="wide">
                  Evacuation notes
                  <textarea
                    rows="2"
                    value={plan.evacuation_notes || ""}
                    onChange={(e) =>
                      setPlan((v) => ({
                        ...v,
                        evacuation_notes: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="wide">
                  Shelter notes
                  <textarea
                    rows="2"
                    value={plan.shelter_notes || ""}
                    onChange={(e) =>
                      setPlan((v) => ({ ...v, shelter_notes: e.target.value }))
                    }
                  />
                </label>
                <label>
                  Review plan every
                  <select value={plan.review_interval_days || 180} onChange={(e) => setPlan((v) => ({ ...v, review_interval_days: e.target.value }))}>
                    <option value="90">3 months</option>
                    <option value="180">6 months</option>
                    <option value="365">Yearly</option>
                  </select>
                </label>
                <footer className="wide">
                  <button
                    type="button"
                    className="quiet"
                    onClick={() => setSheet(null)}
                  >
                    Cancel
                  </button>
                  {plan.confirmation_status === "confirmed" && (
                    <button
                      type="button"
                      className="quiet"
                      onClick={() => setRemovePlanOpen(true)}
                    >
                      Remove plan
                    </button>
                  )}
                  <button disabled={busy}>
                    {busy ? "Saving…" : "Save and mark reviewed"}
                  </button>
                </footer>
              </form>
            )}
            {sheet === "drill" && (
              <form className="hm-safety-form" onSubmit={saveDrill}>
                <label className="wide">Drill name<input required value={drill.title || ""} onChange={(e) => setDrill((v) => ({ ...v, title: e.target.value }))} /></label>
                <label>Type<select value={drill.drill_type || "evacuation"} onChange={(e) => setDrill((v) => ({ ...v, drill_type: e.target.value }))}><option value="evacuation">Evacuation</option><option value="fire">Fire</option><option value="earthquake">Earthquake</option><option value="shelter">Shelter</option><option value="communication">Communication</option><option value="other">Other</option></select></label>
                <label>Date<input required type="date" value={drill.scheduled_on || ""} onChange={(e) => setDrill((v) => ({ ...v, scheduled_on: e.target.value }))} /></label>
                <label>Repeat<select value={drill.repeat_interval_days ?? ""} onChange={(e) => setDrill((v) => ({ ...v, repeat_interval_days: e.target.value }))}><option value="">One time</option><option value="90">Every 3 months</option><option value="180">Every 6 months</option><option value="365">Every year</option></select></label>
                <label className="wide">What to practice<textarea rows="3" value={drill.notes || ""} onChange={(e) => setDrill((v) => ({ ...v, notes: e.target.value }))} /></label>
                <footer className="wide"><button type="button" className="quiet" onClick={() => setSheet(null)}>Cancel</button><button disabled={busy} type="submit">{drill.id ? "Save changes" : "Schedule drill"}</button></footer>
              </form>
            )}
            {sheet === "resource" && (
              <form className="hm-safety-form" onSubmit={addResource}>
                <label>
                  Name
                  <input
                    required
                    list="hm-safety-label-suggestions"
                    value={resource.label}
                    onChange={(e) =>
                      setResource((v) => ({ ...v, label: e.target.value }))
                    }
                  />
                </label>
                <label>
                  Type
                  <select
                    value={resource.resource_type}
                    onChange={(e) =>
                      setResource((v) => ({
                        ...v,
                        resource_type: e.target.value,
                      }))
                    }
                  >
                    {Object.entries(TYPES).map(([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Room / area
                  <select
                    value={resource.property_space_id}
                    onChange={(e) =>
                      setResource((v) => ({
                        ...v,
                        property_space_id: e.target.value,
                      }))
                    }
                  >
                    <option value="">Not assigned</option>
                    {data.spaces.map((row) => (
                      <option value={row.id} key={row.id}>
                        {row.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Vault equipment
                  <select
                    value={resource.vault_item_id}
                    onChange={(e) =>
                      setResource((v) => ({
                        ...v,
                        vault_item_id: e.target.value,
                      }))
                    }
                  >
                    <option value="">No linked item</option>
                    {data.equipment.map((row) => (
                      <option value={row.id} key={row.id}>
                        {row.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  How to access
                  <input
                    value={resource.access_notes}
                    onChange={(e) =>
                      setResource((v) => ({
                        ...v,
                        access_notes: e.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Verify every
                  <select
                    value={resource.verification_interval_days}
                    onChange={(e) =>
                      setResource((v) => ({
                        ...v,
                        verification_interval_days: e.target.value,
                      }))
                    }
                  >
                    <option value="30">Monthly</option>
                    <option value="180">6 months</option>
                    <option value="365">Yearly</option>
                    <option value="">No schedule</option>
                  </select>
                </label>
                <label className="wide">
                  Instructions
                  <textarea
                    rows="2"
                    value={resource.instructions}
                    onChange={(e) =>
                      setResource((v) => ({
                        ...v,
                        instructions: e.target.value,
                      }))
                    }
                  />
                </label>
                <footer className="wide">
                  <button
                    type="button"
                    className="quiet"
                    onClick={() => setSheet(null)}
                  >
                    Cancel
                  </button>
                  <button disabled={busy}>
                    {busy ? "Saving…" : resource.id ? "Save changes" : "Add location"}
                  </button>
                </footer>
              </form>
            )}
            {sheet === "contact" && (
              <form className="hm-safety-form" onSubmit={addContact}>
                <label>
                  Contact name
                  <input
                    required
                    value={contact.name}
                    onChange={(e) =>
                      setContact((v) => ({ ...v, name: e.target.value }))
                    }
                  />
                </label>
                <label>
                  Relationship
                  <input
                    list="hm-safety-relationship-suggestions"
                    value={contact.relationship}
                    onChange={(e) =>
                      setContact((v) => ({
                        ...v,
                        relationship: e.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Phone
                  <input
                    type="tel"
                    value={contact.phone}
                    onChange={(e) =>
                      setContact((v) => ({ ...v, phone: e.target.value }))
                    }
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={contact.email}
                    onChange={(e) =>
                      setContact((v) => ({ ...v, email: e.target.value }))
                    }
                  />
                </label>
                <footer className="wide">
                  <button
                    type="button"
                    className="quiet"
                    onClick={() => setSheet(null)}
                  >
                    Cancel
                  </button>
                  <button disabled={busy}>
                    {busy ? "Saving…" : contact.id ? "Save changes" : "Add contact"}
                  </button>
                </footer>
              </form>
            )}
          </section>
        </div>
      )}
      {contactToRemove && (
        <ConfirmModal
          tag="⚠ REMOVE EMERGENCY CONTACT"
          title="Remove contact?"
          message={`${contactToRemove.name} will be removed from Emergency & Safety. Their original People record, if connected, will not be deleted.`}
          confirmLabel="Remove"
          loading={busy}
          onCancel={() => {
            if (!busy) setContactToRemove(null);
          }}
          onConfirm={async () => {
            if (
              await run(() =>
                emergencySafetyApi.deleteContact(contactToRemove.id),
              )
            ) {
              setContactToRemove(null);
            }
          }}
        />
      )}
      {removePlanOpen && (
        <ConfirmModal
          tag="⚠ REMOVE HOUSEHOLD PLAN"
          title="Remove household plan?"
          message="Meeting points, evacuation notes, and shelter details will be cleared. Emergency contacts and safety locations will be kept."
          confirmLabel="Remove plan"
          loading={busy}
          onCancel={() => { if (!busy) setRemovePlanOpen(false); }}
          onConfirm={async () => {
            if (await run(() => emergencySafetyApi.deletePlan())) {
              setRemovePlanOpen(false);
              setSheet(null);
            }
          }}
        />
      )}
      {showSavingNotice && <SavingNotice message="Saving emergency and safety changes..." />}
    </main>
  );
}
